import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Search, History, CheckCircle, Calendar, Clock, Download, Filter } from 'lucide-react';
import { formatDate, formatTime, formatDateTime } from '../utils/dateFormat';

import { API } from '../lib/apiBase';

export default function DistributionHistoryPage() {
  const { organization } = useAuth();
  const [distributions, setDistributions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('all'); // all, today, week, month
  const [sortOrder, setSortOrder] = useState('desc'); // desc, asc

  // Get date/time format from organization settings
  const dateFormat = organization?.date_format || 'DD/MM/YYYY';
  const timeFormat = organization?.time_format || '24h';

  const fetchDistributions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/beneficiaries`);
      const beneficiaries = response.data;
      
      // Extract all distributions with beneficiary info
      const allDistributions = [];
      beneficiaries.forEach(b => {
        if (b.distributions && b.distributions.length > 0) {
          b.distributions.forEach(d => {
            allDistributions.push({
              ...d,
              beneficiary_id: b.id,
              beneficiary_name: `${b.first_name} ${b.surname}`,
              beneficiary_phone: b.phone_number,
              child_name: b.child_first_name || '-'
            });
          });
        }
      });
      
      setDistributions(allDistributions);
    } catch (error) {
      toast.error('Error loading distribution history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDistributions();
  }, [fetchDistributions]);

  // Filter by date
  const filterByDate = (dist) => {
    if (dateFilter === 'all') return true;
    
    const distDate = new Date(dist.timestamp);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    switch (dateFilter) {
      case 'today':
        const todayEnd = new Date(today);
        todayEnd.setHours(23, 59, 59, 999);
        return distDate >= today && distDate <= todayEnd;
      case 'week':
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        return distDate >= weekAgo;
      case 'month':
        const monthAgo = new Date(today);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        return distDate >= monthAgo;
      default:
        return true;
    }
  };

  // Filter and sort distributions
  const filteredDistributions = distributions
    .filter(d => 
      d.beneficiary_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.beneficiary_phone?.includes(searchTerm)
    )
    .filter(filterByDate)
    .sort((a, b) => {
      const dateA = new Date(a.timestamp);
      const dateB = new Date(b.timestamp);
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });

  // Stats
  const todayCount = distributions.filter(d => {
    const distDate = new Date(d.timestamp);
    const today = new Date();
    return distDate.toDateString() === today.toDateString();
  }).length;

  const weekCount = distributions.filter(d => {
    const distDate = new Date(d.timestamp);
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return distDate >= weekAgo;
  }).length;

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Beneficiary', 'Phone', 'Child', 'Date', 'Time', 'Items', 'Notes'];
    const rows = filteredDistributions.map(d => [
      d.beneficiary_name,
      d.beneficiary_phone,
      d.child_name,
      formatDate(d.timestamp, dateFormat),
      formatTime(d.timestamp, timeFormat),
      d.items_delivered?.join(', ') || '',
      d.notes || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `distribution_history_${formatDate(new Date().toISOString(), 'YYYY-MM-DD')}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    
    toast.success('History exported!');
  };

  return (
    <div className="space-y-6" data-testid="distribution-history-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1A1A1A] font-['Manrope']">Distribution History</h1>
          <p className="text-[#595959] mt-1 text-sm md:text-base">
            Complete log of all check-ins during distributions
          </p>
        </div>
        <Button
          variant="outline"
          onClick={exportToCSV}
          disabled={filteredDistributions.length === 0}
          data-testid="export-history-button"
        >
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-[#E5E5E5]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#E9EFEA] flex items-center justify-center">
                <History className="w-5 h-5 text-[#4A5D4E]" />
              </div>
              <div>
                <p className="text-xs text-[#8C8C8C]">Total</p>
                <p className="text-xl font-bold text-[#1A1A1A]">{distributions.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E5E5E5]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#E8F5E9] flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-[#2E7D32]" />
              </div>
              <div>
                <p className="text-xs text-[#8C8C8C]">Today</p>
                <p className="text-xl font-bold text-[#2E7D32]">{todayCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E5E5E5]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#FFF8E1] flex items-center justify-center">
                <Calendar className="w-5 h-5 text-[#F57F17]" />
              </div>
              <div>
                <p className="text-xs text-[#8C8C8C]">This Week</p>
                <p className="text-xl font-bold text-[#F57F17]">{weekCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E5E5E5]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#E9EFEA] flex items-center justify-center">
                <Filter className="w-5 h-5 text-[#4A5D4E]" />
              </div>
              <div>
                <p className="text-xs text-[#8C8C8C]">Filtered</p>
                <p className="text-xl font-bold text-[#4A5D4E]">{filteredDistributions.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#8C8C8C]" />
          <Input
            placeholder="Search by beneficiary name or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-history-input"
          />
        </div>
        <Select value={dateFilter} onValueChange={setDateFilter}>
          <SelectTrigger className="w-full md:w-[180px]" data-testid="date-filter-select">
            <Calendar className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by date" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Time</SelectItem>
            <SelectItem value="today">Today</SelectItem>
            <SelectItem value="week">Last 7 Days</SelectItem>
            <SelectItem value="month">Last 30 Days</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortOrder} onValueChange={setSortOrder}>
          <SelectTrigger className="w-full md:w-[180px]" data-testid="sort-order-select">
            <Clock className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Sort order" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="desc">Newest First</SelectItem>
            <SelectItem value="asc">Oldest First</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Mobile Card View (hidden on md+) */}
      <div className="md:hidden space-y-2" data-testid="history-mobile-list">
        {loading ? (
          <Card className="border-[#E5E5E5]"><CardContent className="py-8 text-center text-[#8C8C8C]">Loading...</CardContent></Card>
        ) : filteredDistributions.length === 0 ? (
          <Card className="border-[#E5E5E5]">
            <CardContent className="py-8 text-center text-[#8C8C8C]">
              <History className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No distributions found</p>
            </CardContent>
          </Card>
        ) : (
          filteredDistributions.map((dist, idx) => (
            <Card key={`${dist.beneficiary_id}-${dist.id}-${idx}`} className="border border-[#E5E5E5]" data-testid={`history-row-${idx}`}>
              <CardContent className="p-3">
                <div className="flex items-start gap-2">
                  <div className="w-9 h-9 rounded-full bg-[#E9EFEA] flex items-center justify-center text-xs font-medium text-[#4A5D4E] flex-shrink-0">
                    {dist.beneficiary_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[#1A1A1A] text-sm break-words">{dist.beneficiary_name}</p>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-xs text-[#595959]">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(dist.timestamp, dateFormat)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatTime(dist.timestamp, timeFormat)}
                      </span>
                      {dist.beneficiary_phone && <span className="font-mono">{dist.beneficiary_phone}</span>}
                      {dist.child_name && <span>Child: {dist.child_name}</span>}
                    </div>
                    {dist.items_delivered?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {dist.items_delivered.map((item, i) => (
                          <span key={i} className="text-[10px] bg-[#E9EFEA] text-[#4A5D4E] px-1.5 py-0.5 rounded">
                            {item}
                          </span>
                        ))}
                      </div>
                    )}
                    {dist.notes && (
                      <p className="text-xs text-[#595959] italic mt-1 break-words">"{dist.notes}"</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Desktop Table (hidden on mobile) */}
      <Card className="border-[#E5E5E5] hidden md:block">
        <CardContent className="p-0">
          <ScrollArea className="w-full">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F0EFEA]">
                  <TableHead className="font-medium">Beneficiary</TableHead>
                  <TableHead className="font-medium">Phone</TableHead>
                  <TableHead className="font-medium">Child</TableHead>
                  <TableHead className="font-medium">Date</TableHead>
                  <TableHead className="font-medium">Time</TableHead>
                  <TableHead className="font-medium">Items Delivered</TableHead>
                  <TableHead className="font-medium">Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-[#8C8C8C]">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : filteredDistributions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-[#8C8C8C]">
                      <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No distributions found</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredDistributions.map((dist, idx) => (
                    <TableRow key={`${dist.beneficiary_id}-${dist.id}-${idx}`} data-testid={`history-row-desk-${idx}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-[#E9EFEA] flex items-center justify-center text-xs font-medium text-[#4A5D4E]">
                            {dist.beneficiary_name?.split(' ').map(n => n[0]).join('')}
                          </div>
                          <span className="font-medium">{dist.beneficiary_name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-[#595959]">{dist.beneficiary_phone}</TableCell>
                      <TableCell>{dist.child_name}</TableCell>
                      <TableCell>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3 text-[#8C8C8C]" />
                          {formatDate(dist.timestamp, dateFormat)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3 text-[#8C8C8C]" />
                          {formatTime(dist.timestamp, timeFormat)}
                        </span>
                      </TableCell>
                      <TableCell>
                        {dist.items_delivered?.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {dist.items_delivered.map((item, i) => (
                              <span key={i} className="text-xs bg-[#E9EFEA] text-[#4A5D4E] px-2 py-0.5 rounded">
                                {item}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-[#8C8C8C]">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-[#595959] max-w-[200px] truncate">
                        {dist.notes || '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
